import { Helmet } from "react-helmet-async";

const SITE_URL = "https://adsolute.co.uk";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_instant-seo-score/artifacts/h319f9hx_Adsolute.png";

// Inject a JSON-LD script tag via Helmet
const JsonLd = ({ data }) => (
  <Helmet>
    <script type="application/ld+json">{JSON.stringify(data)}</script>
  </Helmet>
);

// ============= REUSABLE SCHEMA BUILDERS =============

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Adsolute",
  "url": SITE_URL,
  "logo": LOGO_URL,
  "description": "UK-based Business Process Outsourcing provider. Data entry, accounts payable/receivable, back office support, customer support, HR and IT outsourcing services.",
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+44-748-1111-540",
    "contactType": "customer service",
    "areaServed": "GB",
    "availableLanguage": "English"
  },
  "sameAs": [
    "https://www.instagram.com/adsolute.co.uk",
    "https://www.facebook.com/profile.php?id=61579587053584",
    "https://www.linkedin.com/company/adsolute-co-uk-limited"
  ]
};

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": "Adsolute",
  "image": LOGO_URL,
  "url": SITE_URL,
  "telephone": "+44-748-1111-540",
  "email": "support@adsolute.co.uk",
  "priceRange": "££",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "9 Norton Road",
    "addressLocality": "Camberley",
    "addressRegion": "Surrey",
    "postalCode": "GU15 1SB",
    "addressCountry": "GB"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 51.3359,
    "longitude": -0.7509
  },
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    "opens": "09:00",
    "closes": "18:00"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.9",
    "bestRating": "5",
    "worstRating": "1",
    "ratingCount": "127",
    "reviewCount": "89"
  }
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Adsolute",
  "url": SITE_URL,
  "potentialAction": {
    "@type": "SearchAction",
    "target": `${SITE_URL}/blog?q={search_term_string}`,
    "query-input": "required name=search_term_string"
  }
};

export const buildServiceSchema = (name, description, url) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": name,
  "description": description,
  "url": `${SITE_URL}${url}`,
  "provider": {
    "@type": "Organization",
    "name": "Adsolute",
    "url": SITE_URL
  },
  "areaServed": {
    "@type": "Country",
    "name": "United Kingdom"
  },
  "serviceType": name
});

export const buildFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.q,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.a
    }
  }))
});

export const buildBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, idx) => ({
    "@type": "ListItem",
    "position": idx + 1,
    "name": item.name,
    "item": `${SITE_URL}${item.path}`
  }))
});

export const buildArticleSchema = (blog) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": blog.title,
  "description": blog.meta_description || blog.excerpt || "",
  "image": blog.featured_image || LOGO_URL,
  "url": `${SITE_URL}/blog/${blog.slug}`,
  "datePublished": blog.created_at,
  "dateModified": blog.updated_at || blog.created_at,
  "author": {
    "@type": "Organization",
    "name": "Adsolute",
    "url": SITE_URL
  },
  "publisher": {
    "@type": "Organization",
    "name": "Adsolute",
    "logo": {
      "@type": "ImageObject",
      "url": LOGO_URL
    }
  }
});

export const contactPageSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "name": "Contact Adsolute",
  "url": `${SITE_URL}/contact`,
  "description": "Get in touch with Adsolute, London's premier digital marketing agency.",
  "mainEntity": {
    "@type": "Organization",
    "name": "Adsolute",
    "telephone": "+44-748-1111-540",
    "email": "support@adsolute.co.uk"
  }
};

export default JsonLd;
